import React, { useEffect } from 'react';
import './MobileLandingPage.css';
import './LandingBase.css';
import './MobileLandingPage.new.css';
import HeroSection from './sections/HeroSection';
import FeaturesSection from './sections/FeaturesSection';
import HowItWorksSection from './sections/HowItWorksSection';
import TestimonialsSection from './sections/TestimonialsSection';
import PricingSection from './sections/PricingSection';
import initSnapSections from './snapSections';
import MobileInfoPanel from '../../../components/mobile/MobileInfoPanel';

// Mobile Landing Page with Snapboard layout (legacy version for ?layout=snapboard)
export default function MobileLandingPageSnapboard(){
  // Decorative parallax blobs
  useEffect(() => {
    // Dynamically assign --nav-h to match header height for compact info panel positioning
    function setNavHeight(){
      const header = document.querySelector('header, .app-header, .main-nav');
      const h = header ? header.getBoundingClientRect().height : 64;
      document.documentElement.style.setProperty('--nav-h', h + 'px');
    }

    // Set dynamic viewport unit (--vh) to mitigate iOS Safari chrome showing/hiding
    function setVh(){
      // Use visualViewport if available for more accurate height excluding overlays
      const vhSource = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      const vh = vhSource * 0.01; // 1% unit
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    }
    setVh();
    // Update on resize, orientation change, and visualViewport resize (iOS Safari)
    window.addEventListener('resize', setVh, { passive:true });
    window.addEventListener('orientationchange', setVh, { passive:true });
    if(window.visualViewport){ window.visualViewport.addEventListener('resize', setVh); }
    // Delay re-check after potential browser UI settle
    setTimeout(setVh, 250);
    setTimeout(setVh, 1000);
    setNavHeight();
    window.addEventListener('resize', setNavHeight);
    // micro timeout to catch late layout shifts
    requestAnimationFrame(() => setNavHeight());
    // Apply 'vh-tight' class to hero when viewport height is constrained (< 700px)
    function applyVhTight(){
      const hero = document.querySelector('.m-hero');
      if(!hero) return;
      const h = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      if(h < 700){ hero.classList.add('vh-tight'); } else { hero.classList.remove('vh-tight'); }
    }
    applyVhTight();
    window.addEventListener('resize', applyVhTight, { passive:true });
    if(window.visualViewport){ window.visualViewport.addEventListener('resize', applyVhTight); }
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduce) {
      const blobs = () => Array.from(document.querySelectorAll('.m-blob'));
      let ticking = false;
      function onScroll(){
        if(!ticking){
          requestAnimationFrame(()=>{
            const y = window.scrollY || 0;
            blobs().forEach((b,i)=>{
              const factor = i === 0 ? 0.012 : 0.018;
              b.style.transform = `translate3d(${y*factor}px, ${y*factor*0.55}px, 0)`;
            });
            ticking = false;
          });
          ticking = true;
        }
      }
  window.addEventListener('scroll', onScroll, { passive:true });
    }
    return () => {
      window.removeEventListener('resize', setVh);
      window.removeEventListener('orientationchange', setVh);
      if(window.visualViewport){ window.visualViewport.removeEventListener('resize', setVh); }
      window.removeEventListener('resize', applyVhTight);
      if(window.visualViewport){ window.visualViewport.removeEventListener('resize', applyVhTight); }
    };
  }, []);

  // Tilt effect for Scout image (delegated) – minimal global listener
  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    const scout = () => document.querySelector('.tilt');
    const onMove = (e) => {
      const t = scout(); if(!t) return;
      const point = e.touches ? e.touches[0] : e;
      const x = point.clientX / window.innerWidth - 0.5;
      const y = point.clientY / window.innerHeight - 0.5;
      t.style.transform = `translateZ(0) rotateX(${(y * -6).toFixed(2)}deg) rotateY(${(x * 6).toFixed(2)}deg)`;
    };
    const reset = () => { const t = scout(); if(t) t.style.transform = 'translateZ(0)'; };
    window.addEventListener('mousemove', onMove, { passive:true });
    window.addEventListener('touchmove', onMove, { passive:true });
    window.addEventListener('mouseleave', reset);
    window.addEventListener('touchend', reset);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('mouseleave', reset);
      window.removeEventListener('touchend', reset);
    };
  }, []);

  // Info panel morph logic (Web Animations API) injected after mount
  useEffect(() => {
    (function(){
      const root = document.documentElement;
      const header = document.querySelector('.navbar, header, .site-header');
      const launcher = document.querySelector('.info-launcher');
      const panel = document.getElementById('info-panel');
  if(!launcher || !panel) return;

      function setNavHeight(){
        const h = header ? Math.round(header.getBoundingClientRect().height) : 64;
        root.style.setProperty('--nav-h', h + 'px');
      }
      setNavHeight();
      window.addEventListener('resize', setNavHeight, { passive:true });
      window.addEventListener('orientationchange', setNavHeight, { passive:true });

      let animating = false;
      let panelOpen = false;
      function setIconState(){
        if(panelOpen){
          launcher.dataset.state = 'open';
          launcher.setAttribute('aria-label','Close information');
          launcher.setAttribute('aria-expanded','true');
        } else {
          launcher.dataset.state = 'closed';
          launcher.setAttribute('aria-label','Open information');
          launcher.setAttribute('aria-expanded','false');
        }
      }
      function openPanel(){
        const l = launcher.getBoundingClientRect();
        const p = panel.getBoundingClientRect();
        const fromX = (l.left + l.width/2) - (p.left + 16);
        const fromY = (l.top + l.height/2) - (p.top + 8);
        animating = true;
        const openAnim = panel.animate([
          { transform:`translate(${fromX}px, ${fromY}px) scale(.85)`, opacity:0 },
          { transform:'translate(0,0) scale(1)', opacity:1 }
        ], { duration:220, easing:'ease-out', fill:'both' });
        openAnim.onfinish = () => { animating = false; };
    panel.classList.add('is-open');
    panelOpen = true;
    launcher.classList.add('is-active');
    panel.setAttribute('aria-hidden','false');
    setIconState();
      }

      function closePanel(){
        const l = launcher.getBoundingClientRect();
        const p = panel.getBoundingClientRect();
        const toX = (l.left + l.width/2) - (p.left + 16);
        const toY = (l.top + l.height/2) - (p.top + 8);
        animating = true;
        const anim = panel.animate([
          { transform:'translate(0,0) scale(1)', opacity:1 },
          { transform:`translate(${toX}px, ${toY}px) scale(.85)`, opacity:0 }
        ], { duration:180, easing:'ease-in', fill:'forwards' });
        anim.onfinish = () => {
          panel.classList.remove('is-open');
          launcher.classList.remove('is-active');
          panel.setAttribute('aria-hidden','true');
          panel.style.transform = ''; panel.style.opacity = '';
          animating = false;
          panelOpen = false;
          setIconState();
        };
      }

      launcher.addEventListener('click', (e)=>{
        e.stopPropagation();
        if(animating) return;
        panelOpen ? closePanel() : openPanel();
      });
      // Removed outside click auto-close per simplified requirement.
    })();
  }, []);

  // Initialize snapboard sections behavior
  useEffect(() => {
    const cleanup = initSnapSections('#snap-root');
    return () => {
      if (cleanup && cleanup.observer) {
        cleanup.observer.disconnect();
      }
    };
  }, []);

  return (
    <div className="m-gradient-canvas m-landing-root">
      <div aria-hidden className="m-blob m-blob-a" />
      <div aria-hidden className="m-blob m-blob-b" />
      <div className="m-top-gradient" aria-hidden></div>
      <div id="snap-root" className="snap-root">
        <HeroSection />
        <FeaturesSection />
        <HowItWorksSection />
        <TestimonialsSection />
        <PricingSection />
      </div>
      {/* Floating Info Panel */}
      <MobileInfoPanel />
    </div>
  );
}


